import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import ConsentField from '../../components/ConsentField/ConsentField';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { EventApplicationSubmitted } from '../../helpers/gtm';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSetRightPanelContent from '../../hooks/useSetRightPanelContent';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { getApplicationState, submitCoApplicant } from '../../store/application/application';
import { addOrUpdateFormField, invalidateForm } from '../../store/applicationform';
import { setCurrentPage, WizardPageAction } from '../../store/wizard/wizard';
import CoApplicantSubmitPageSidePanel from './CoApplicantSubmitPageSidePanel';

const CoApplicantSubmitPage = () => {
    useComponentTracker('CoApplicantSubmitPage');
    const translate = useTranslation();
    const theme = useTheme();
    const greaterThanLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { coApplicantTotalSections } = usePagesConfig();
    const dispatch = useAppDispatch();
    const { currentPage } = usePagesContext();
    const applicationId = useAppSelector((state) => state.application.applicationId);
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const { consentDetails, isError } = useGetConsentService('TERMS_LOANS');
    const defaultPagesAction = useDefaultPagesAction();
    const applicationReferenceNumber = useArnFromPath();
    const { countryCode } = useSettings();

    useSetBannerType('none');

    const sidePanelContent = useMemo(() => <CoApplicantSubmitPageSidePanel />, []);
    useSetRightPanelContent(greaterThanLg ? sidePanelContent : null);

    const requiredFieldConsents =
        consentDetails
            ?.filter((consentDetails) => consentDetails.required)
            .map((consentDetails) => consentDetails.name) ?? [];

    const validationErrors = useValidation(requiredFieldConsents);

    useEffect(() => {
        setErrorOccurred(false);
    }, []);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'submit-coapplicant') {
            if (validationErrors.length) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }

            try {
                await dispatch(submitCoApplicant({ applicationReferenceNumber: applicationReferenceNumber! })).unwrap();
                dispatch(invalidateForm());
                await dispatch(getApplicationState({ applicationReferenceNumber }));
                dispatch(setCurrentPage(0));
            } catch (error) {
                setErrorOccurred(true);
                throw error;
            } finally {
                EventApplicationSubmitted(applicationId, countryCode, 'coapplicant');
            }
            return false;
        } else {
            return defaultPagesAction(action);
        }
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={2}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={1}
                currentPageInSection={1}
                sectionTitle={translate('field.section.about-the-loan')}
            />
            <Title text={translate('page.submit.title')} />

            {!greaterThanLg && <CoApplicantSubmitPageSidePanel />}

            <Subtitle text={translate('page.submit.subtitle.consent')} />
            {consentDetails?.map((consentDetail) => (
                <ConsentField key={consentDetail.name} consentDetail={consentDetail} />
            ))}
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.submit.error.title`)}
                    description={translate(`page.submit.error.description`)}
                />
            )}
            {isError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={['submit-coapplicant']}
                onAction={onAction}
                currentSection={2}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={1}
                currentPageInSection={1}
                sectionTitle={translate('field.section.about-the-loan')}
            />
        </>
    );
};

export default CoApplicantSubmitPage;
